import { mergeClasses } from "@fluentui/react-components";
import {
  ArrowTrendingRegular,
  ChevronRightRegular,
  OrganizationRegular,
  PersonRegular,
} from "@fluentui/react-icons";
import { RetrospectiveCaseStudyPanel } from "apps/project-iq/components/RetrospectiveCaseStudyPanel/RetrospectiveCaseStudyPanel";
import { useState } from "react";
import { useRetrospectivePageStyles } from "./RetrospectivePage.styles";
import { IRetrospectivePageProps } from "./RetrospectivePage.types";

const QUESTIONS_SECTION_BODY_ID = "retrospective-questions-body";

export const RetrospectivePage = ({
  workspaceId,
}: IRetrospectivePageProps): JSX.Element => {
  const styles = useRetrospectivePageStyles();
  const [isQuestionsSectionExpanded, setIsQuestionsSectionExpanded] =
    useState(false);

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <p className={styles.intro}>
          Use this page to conduct a structured project retrospective aligned
          with North Highland's Delivery Excellence framework. Reflect on what
          the team learned, how individuals grew, and where the delivery process
          can improve for future engagements.
        </p>

        <div className={styles.questionsSection}>
          <button
            type="button"
            className={styles.questionsSectionHeaderButton}
            onClick={() => setIsQuestionsSectionExpanded((current) => !current)}
            aria-expanded={isQuestionsSectionExpanded}
            aria-controls={QUESTIONS_SECTION_BODY_ID}
          >
            <span
              className={mergeClasses(
                styles.questionsSectionChevron,
                isQuestionsSectionExpanded &&
                  styles.questionsSectionChevronExpanded,
              )}
              aria-hidden="true"
            >
              <ChevronRightRegular />
            </span>
            <span className={styles.questionsSectionTitle}>
              Retrospective Guide
            </span>
          </button>

          {isQuestionsSectionExpanded && (
            <div
              id={QUESTIONS_SECTION_BODY_ID}
              className={styles.questionsSectionBody}
            >
              <div className={styles.cardRow}>
                <div className={styles.card}>
                  <div className={styles.cardIcon} aria-hidden="true">
                    <OrganizationRegular fontSize={18} />
                  </div>
                  <h2 className={styles.cardTitle}>
                    What we learned about the client
                  </h2>
                  <p className={styles.cardBody}>
                    Capture institutional knowledge about the client that will
                    inform future North Highland engagements.
                  </p>
                  <ul className={styles.cardList}>
                    <li className={styles.cardListItem}>
                      What stakeholder dynamics shaped how the work was
                      prioritized?
                    </li>
                    <li className={styles.cardListItem}>
                      What surprised the team about the client's culture or
                      decision-making process?
                    </li>
                    <li className={styles.cardListItem}>
                      What client strengths can future teams build on?
                    </li>
                    <li className={styles.cardListItem}>
                      What should the next team know before starting?
                    </li>
                  </ul>
                </div>

                <div className={styles.card}>
                  <div className={styles.cardIcon} aria-hidden="true">
                    <PersonRegular fontSize={18} />
                  </div>
                  <h2 className={styles.cardTitle}>
                    Team member goal reflection
                  </h2>
                  <p className={styles.cardBody}>
                    Invite each team member to reflect on their personal
                    development goals and growth during the engagement.
                  </p>
                  <ul className={styles.cardList}>
                    <li className={styles.cardListItem}>
                      What goals did you set at the start of this engagement?
                    </li>
                    <li className={styles.cardListItem}>
                      Which goals were achieved, and which were not?
                    </li>
                    <li className={styles.cardListItem}>
                      What new skills or capabilities did you develop?
                    </li>
                    <li className={styles.cardListItem}>
                      What gaps remain and how will you address them next?
                    </li>
                  </ul>
                </div>

                <div className={styles.card}>
                  <div className={styles.cardIcon} aria-hidden="true">
                    <ArrowTrendingRegular fontSize={18} />
                  </div>
                  <h2 className={styles.cardTitle}>Improvement areas</h2>
                  <p className={styles.cardBody}>
                    Identify concrete changes the team would make to improve
                    delivery quality and collaboration on the next engagement.
                  </p>
                  <ul className={styles.cardList}>
                    <li className={styles.cardListItem}>
                      Where did the delivery process create friction or slow the
                      team down?
                    </li>
                    <li className={styles.cardListItem}>
                      Were there communication gaps — internally or with the
                      client?
                    </li>
                    <li className={styles.cardListItem}>
                      What tools, methods, or practices should the team adopt or
                      drop?
                    </li>
                    <li className={styles.cardListItem}>
                      What would you do differently from day one?
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={styles.caseStudyWrapper}>
          <RetrospectiveCaseStudyPanel workspaceId={workspaceId} />
        </div>
      </div>
    </div>
  );
};
